import { computed } from 'vue';

/**
 * Composable for managing condition filters
 * Handles displaying active filters and performing add/remove/update operations
 *
 * @param TConfig - shape of a filter config entry (must have a `key: string` field)
 * @param TValue  - union of all possible filter value types for this condition
 */
export function useConditionFilters<
  TConfig extends { key: string },
  TValue = unknown,
>(
  getFilters: () => Partial<Record<string, TValue>>,
  getFilterConfigs: () => TConfig[],
) {
  /**
   * Get active filters in insertion order
   */
  const activeFilters = computed(() => {
    const filters = getFilters();
    const filterConfigs = getFilterConfigs();

    // Get keys in insertion order and ensure non-null values
    const activeKeys = Object.keys(filters).filter(
      (key) => filters[key] != null,
    );

    // Map to configs while preserving order
    return activeKeys
      .map((key) => {
        const config = filterConfigs.find((c) => c.key === key);
        if (!config) return null;
        return {
          ...config,
          value: filters[key] as TValue,
        };
      })
      .filter((item): item is TConfig & { value: TValue } => item !== null);
  });

  /**
   * Add or remove a filter from the filters object
   */
  const addOrRemove = <TFilter>(
    filters: Record<string, TFilter | null>,
    filterType: string,
    filterValue: TFilter | null,
  ): Record<string, TFilter> => {
    if (filterValue !== null) {
      // Adding: spread adds new key at the end
      return {
        ...filters,
        [filterType]: filterValue,
      };
    } else {
      // Removing: destructure to exclude the filter
      const { [filterType]: _removed, ...remainingFilters } = filters;
      return remainingFilters as Record<string, TFilter>;
    }
  };

  /**
   * Update an existing filter value (preserves order)
   */
  const update = <TFilter>(
    filters: Record<string, TFilter>,
    filterType: string,
    filterValue: TFilter,
  ): Record<string, TFilter> => {
    return {
      ...filters,
      [filterType]: filterValue,
    };
  };

  return {
    activeFilters,
    addOrRemove,
    update,
  };
}
